import React, { useEffect, Children, memo } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Lightbulb, TrendingUp, Users, Globe, Ticket, Building2, PartyPopper, Heart, Star } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const valores = [{
  icon: <Lightbulb className="w-8 h-8 text-emerald-400" data-id="element-685" />,
  title: 'Innovación Constante',
  description: 'Desarrollamos tecnología de vanguardia para anticiparnos a las necesidades del mercado y ofrecer soluciones únicas.'
}, {
  icon: <ShieldCheck className="w-8 h-8 text-emerald-400" data-id="element-686" />,
  title: 'Seguridad y Confianza',
  description: 'Protegemos cada transacción y dato con los más altos estándares de seguridad de la industria.'
}, {
  icon: <TrendingUp className="w-8 h-8 text-emerald-400" data-id="element-687" />,
  title: 'Escalabilidad',
  description: 'Nuestras plataformas están diseñadas para crecer contigo, desde pequeños eventos hasta estadios repletos.'
}, {
  icon: <Users className="w-8 h-8 text-emerald-400" data-id="element-688" />,
  title: 'Enfoque en el Cliente',
  description: 'Tu éxito es el nuestro. Brindamos soporte personalizado y adaptamos nuestras soluciones a tu realidad.'
}];
const verticales = [{
  icon: <Users className="w-6 h-6 text-emerald-400" data-id="element-689" />,
  title: 'Eventos Masivos',
  description: 'Gestión de alta concurrencia y control de accesos fluido.'
}, {
  icon: <Ticket className="w-6 h-6 text-emerald-400" data-id="element-690" />,
  title: 'Clubes de Fútbol',
  description: 'Digitalización de socios, abonos y venta de entradas.'
}, {
  icon: <Globe className="w-6 h-6 text-emerald-400" data-id="element-691" />,
  title: 'Asociaciones y Federaciones',
  description: 'Plataformas unificadas para múltiples sedes y disciplinas.'
}, {
  icon: <PartyPopper className="w-6 h-6 text-emerald-400" data-id="element-692" />,
  title: 'Pequeños Eventos',
  description: 'Setup rápido, venta online y control de acceso con smartphone.'
}, {
  icon: <Heart className="w-6 h-6 text-emerald-400" data-id="element-693" />,
  title: 'Organizaciones sin Fines de Lucro',
  description: 'Herramientas para eventos solidarios y recaudación de fondos.'
}, {
  icon: <Star className="w-6 h-6 text-emerald-400" data-id="element-694" />,
  title: 'Programas de Fidelización',
  description: 'Puntos, recompensas y beneficios para fidelizar tu audiencia.'
}, {
  icon: <Building2 className="w-6 h-6 text-emerald-400" data-id="element-695" />,
  title: 'Control de Accesos y Visitas',
  description: 'Registro y control de visitantes para edificios, condominios e instituciones.'
}];
export function SobreNosotrosPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-slate-950 text-slate-50 font-sans selection:bg-emerald-500/30" data-id="element-696">
      <Navbar data-id="element-697" />

      <main data-id="element-698">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden" data-id="element-699">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-emerald-900/20 via-slate-950 to-slate-950 -z-10" data-id="element-700" />

          <div className="container mx-auto px-4 md:px-6" data-id="element-701">
            <motion.div className="max-w-4xl mx-auto text-center" initial="hidden" animate="visible" variants={fadeInUp} data-id="element-702">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6" data-id="element-703">
                <Ticket className="w-4 h-4" data-id="element-704" />
                TicketMaestro SAS
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight" data-id="element-705">
                Tecnología integral para{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400" data-id="element-706">
                  eventos, clubes e instituciones
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-3xl mx-auto" data-id="element-707">
                Desarrollamos un ecosistema completo de software y equipamiento
                para la venta de entradas, gestión de socios, control de accesos
                y programas de fidelización, adaptado a cada tipo de
                organización.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Misión Section */}
        <section className="py-20 bg-slate-900/50 border-y border-slate-800/50" data-id="element-708">
          <div className="container mx-auto px-4 md:px-6" data-id="element-709">
            <motion.div className="max-w-4xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeInUp} data-id="element-710">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-711">
                Nuestra Misión
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-6" data-id="element-712">
                Proveer a organizadores de eventos, clubes deportivos,
                federaciones, asociaciones y organizaciones sin fines de lucro
                un ecosistema tecnológico completo que integra plataformas
                digitales multicanal — portal web, app móvil, kioscos de
                autoatención y WhatsApp — con equipamiento profesional como
                lectores QR, torniquetes, tótems, boleterías y handhelds.
              </p>
              <p className="text-slate-400 text-lg leading-relaxed" data-id="element-713">
                Nuestra misión es simplificar la complejidad operativa de la
                venta de entradas, la gestión de socios, el control de accesos y
                los programas de fidelización, permitiendo a nuestros clientes
                enfocarse en lo que realmente importa: crear experiencias
                memorables.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Valores Section */}
        <section className="py-20" data-id="element-714">
          <div className="container mx-auto px-4 md:px-6" data-id="element-715">
            <motion.div className="mb-16 text-center" initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeInUp} data-id="element-716">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-717">
                Nuestros Valores
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto" data-id="element-718">
                Los principios que guían nuestro desarrollo tecnológico y la
                relación con cada uno de nuestros clientes.
              </p>
            </motion.div>

            <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={staggerContainer} data-id="element-719">
              {valores.map((valor, index) => <motion.div key={index} variants={fadeInUp} className="bg-slate-900/40 border border-slate-800 rounded-2xl p-8 hover:bg-slate-900 transition-colors group" data-id="element-720">
                  <div className="mb-6 p-3 bg-emerald-500/10 rounded-xl inline-block text-emerald-400 group-hover:scale-110 transition-transform duration-300" data-id="element-721">
                    {valor.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-slate-100" data-id="element-722">
                    {valor.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed text-sm" data-id="element-723">
                    {valor.description}
                  </p>
                </motion.div>)}
            </motion.div>
          </div>
        </section>

        {/* Lo que hacemos Section */}
        <section className="py-20 bg-slate-900/50 border-t border-slate-800/50" data-id="element-724">
          <div className="container mx-auto px-4 md:px-6" data-id="element-725">
            <motion.div className="mb-16 text-center" initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeInUp} data-id="element-726">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-727">
                Ecosistema Integral
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto" data-id="element-728">
                Proveemos una suite completa de software (Apps, Web, Kioscos,
                WhatsApp) y hardware (Lectores QR, Torniquetes, POS) adaptada a
                diferentes industrias.
              </p>
            </motion.div>

            <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={staggerContainer} data-id="element-729">
              {verticales.map((item, index) => <motion.div key={index} variants={fadeInUp} className="bg-slate-950 border border-slate-800 rounded-xl p-6 flex flex-col items-center text-center hover:border-emerald-500/50 transition-colors" data-id="element-730">
                  <div className="mb-4 p-3 bg-slate-900 rounded-full border border-slate-800" data-id="element-731">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-slate-200" data-id="element-732">
                    {item.title}
                  </h3>
                  <p className="text-sm text-slate-400" data-id="element-733">{item.description}</p>
                </motion.div>)}
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 relative overflow-hidden" data-id="element-734">
          <div className="absolute inset-0 bg-emerald-900/20" data-id="element-735" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-emerald-500/10 via-transparent to-transparent" data-id="element-736" />

          <div className="container mx-auto px-4 relative z-10" data-id="element-737">
            <motion.div className="max-w-3xl mx-auto text-center bg-slate-900/80 backdrop-blur-xl border border-slate-700 p-10 md:p-16 rounded-3xl shadow-2xl" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} data-id="element-738">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-739">
                ¿Listo para llevar tu organización al siguiente nivel?
              </h2>
              <p className="text-lg text-slate-300 mb-8" data-id="element-740">
                Únete a los cientos de organizadores, empresas e instituciones
                que confían en TicketMaestro.
              </p>
              <Button variant="primary" size="lg" className="text-lg px-8 py-4" onClick={() => window.location.href = 'mailto:contacto@ticketmaestro.net'} data-id="element-741">
                Contactar Ventas
              </Button>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-742" />
    </div>;
}